/**
 * AX-Agent 그리드 레지스트리 — 활성 AxDataGrid 의 메타/스냅샷을 임베드 브리지에 노출.
 *
 * 설계: axadmin docs/designs/mes-agent-screen-interaction.md §5.2.
 * - AxDataGrid 가 마운트 시 자동 등록(페이지 코드 0줄), 언마운트 시 해제.
 * - 라우트당 다중 그리드면 마지막 상호작용(클릭) 그리드가 활성.
 * - 스냅샷은 AG Grid 의 "표시 상태" 기준: 필터·정렬 반영, 표시 컬럼만,
 *   `agentExclude` 컬럼 제외, 행 수 상한·셀 500자 절단 (§5.6).
 * - rows 는 push 하지 않는다 — 브리지가 ax:grid:request 수신 시에만 pull.
 */
import type { GridApi } from 'ag-grid-community';
import { ref } from 'vue';

/** ag-grid 선택 체크박스 pseudo 컬럼 id (SELECTION_COLUMN_ID 미러 — 값 계약은 ag-grid 문서 고정) */
const SELECTION_COLUMN = 'ag-Grid-SelectionColumn';
/** AxDataGrid 그룹 헤더 pseudo-row 플래그 (AxDataGrid.vue AX_GROUP_ROW_FLAG 미러) */
const GROUP_ROW_FLAG = '__axGroupRow';

const DEFAULT_MAX_ROWS = 500;
const HARD_MAX_ROWS = 1000;
const MAX_CELL_CHARS = 500;

export interface AgentGridColumn {
  field: string;
  label: string;
}

export interface AgentGridMeta {
  id: string;
  title?: string;
  columns: AgentGridColumn[];
  row_count: number;
}

export interface AgentGridSnapshot {
  grid_id: string;
  columns: AgentGridColumn[];
  rows: Record<string, unknown>[];
  total_rows: number;
  truncated: boolean;
}

export interface AgentGridSource {
  id: string;
  title?: string;
  getApi: () => GridApi | null;
  /** server(infinite) 모드 — 로드된 캐시 범위만 스냅샷 가능(truncated) */
  isServer: () => boolean;
  isExcluded: (field: string) => boolean;
}

const registry = new Map<string, AgentGridSource>();
let activeId: string | null = null;

/** 등록/해제/활성 전환마다 증가 — 브리지가 watch 해 ax:context 를 재전송한다. */
export const agentGridVersion = ref(0);

export function registerAgentGrid(source: AgentGridSource): { id: string; unregister: () => void } {
  let id = source.id || 'grid';
  let suffix = 2;
  while (registry.has(id)) id = `${source.id || 'grid'}-${suffix++}`;
  registry.set(id, { ...source, id });
  activeId = id;
  agentGridVersion.value++;
  return {
    id,
    unregister: () => {
      registry.delete(id);
      if (activeId === id) {
        const remaining = [...registry.keys()];
        activeId = remaining.length ? (remaining[remaining.length - 1] ?? null) : null;
      }
      agentGridVersion.value++;
    },
  };
}

/**
 * 사용자 상호작용(클릭)·GridApi 준비 시 호출 — 활성 승격 + 버전 증가.
 * 등록은 setup 시점(api 아직 null)이라, onGridReady 에서 이걸 다시 불러줘야
 * 브리지가 api 가 생긴 뒤의 그리드 메타를 ax:context 로 재전송할 수 있다.
 */
export function touchAgentGrid(id: string): void {
  if (!registry.has(id)) return;
  activeId = id;
  agentGridVersion.value++;
}

function resolveGrid(gridId?: string): AgentGridSource | null {
  if (gridId) return registry.get(gridId) ?? null;
  if (activeId) return registry.get(activeId) ?? null;
  return null;
}

function visibleColumns(source: AgentGridSource, api: GridApi): AgentGridColumn[] {
  const columns: AgentGridColumn[] = [];
  for (const column of api.getAllDisplayedColumns() ?? []) {
    if (column.getColId() === SELECTION_COLUMN) continue;
    const colDef = column.getColDef() as { field?: string; headerName?: string };
    if (!colDef.field) continue;
    if (source.isExcluded(colDef.field)) continue;
    columns.push({ field: colDef.field, label: colDef.headerName || colDef.field });
  }
  return columns;
}

function isGroupPseudoRow(data: unknown): boolean {
  return typeof data === 'object' && data !== null && GROUP_ROW_FLAG in data;
}

function cellValue(value: unknown): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value;
  const text = typeof value === 'string' ? value : safeStringify(value);
  return text.length > MAX_CELL_CHARS ? `${text.slice(0, MAX_CELL_CHARS)}…` : text;
}

function safeStringify(value: unknown): string {
  if (typeof value !== 'object' || value === null) return String(value);
  try {
    return JSON.stringify(value) ?? '[unserializable]';
  } catch {
    return '[unserializable]';
  }
}

/** 브리지 ax:context 용 — 활성 그리드 메타 (rows 미포함). */
export function agentGridContextMeta(): AgentGridMeta | null {
  const source = resolveGrid();
  const api = source?.getApi();
  if (!source || !api) return null;
  let rowCount = 0;
  api.forEachNode((node) => {
    if (node.data != null && !isGroupPseudoRow(node.data)) rowCount++;
  });
  return {
    id: source.id,
    ...(source.title !== undefined ? { title: source.title } : {}),
    columns: visibleColumns(source, api),
    row_count: rowCount,
  };
}

/** 브리지 ax:grid:request 응답용 — 표시 상태 스냅샷. */
export function snapshotAgentGrid(gridId?: string, maxRows?: number): AgentGridSnapshot | null {
  const source = resolveGrid(gridId);
  const api = source?.getApi();
  if (!source || !api) return null;

  const cap = Math.min(Math.max(1, maxRows ?? DEFAULT_MAX_ROWS), HARD_MAX_ROWS);
  const columns = visibleColumns(source, api);
  const fields = columns.map((column) => column.field);
  const rows: Record<string, unknown>[] = [];
  let total = 0;

  const collect = (data: unknown): void => {
    if (data == null || isGroupPseudoRow(data)) return;
    total++;
    if (rows.length >= cap) return;
    const record = data as Record<string, unknown>;
    const row: Record<string, unknown> = {};
    for (const field of fields) row[field] = cellValue(record[field]);
    rows.push(row);
  };

  if (source.isServer()) {
    // infinite 모델 — 로드된 캐시 범위만 순회 가능
    api.forEachNode((node) => collect(node.data));
  } else {
    api.forEachNodeAfterFilterAndSort((node) => collect(node.data));
  }

  return {
    grid_id: source.id,
    columns,
    rows,
    total_rows: total,
    // server 모드는 캐시 밖 데이터가 있을 수 있어 항상 truncated 로 정직 표기
    truncated: source.isServer() || total > rows.length,
  };
}
